import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { EnginePostureView } from '@term-deck/core/engine-posture.js';
import { api, ApiError } from '../api/client';

/**
 * M435/M436 (gate 81.D) — A PAUSA GLOBAL DA AUTONOMIA NA TELA.
 *
 * O backend ja decidia e ja persistia; o que faltava era o DONO poder armar e desarmar sem um agente
 * no meio, e QUALQUER UM poder ver que a postura existe. Sem esta superficie, a unica forma de saber
 * que a frota esta travada de proposito era levar um 403 tentando ligar um motor — descobrir a regra
 * pela recusa e o oposto de tornar a decisao visivel.
 *
 * POR QUE ESTE PAINEL VIVE NO `/chat`, ACIMA DOS MOTORES, e nao numa aba do AdminPanel: a postura
 * governa exatamente os seis interruptores que estao na secao "motores autonomos" do header logo acima.
 * Quem chega aqui para ligar o autopilot precisa ver, no mesmo campo de visao, que existe uma ordem
 * dizendo o contrario — e por que. O AdminPanel e um modal de administracao (tokens, 2FA, audit,
 * backup): mandar o dono abrir outra tela para entender por que o botao ao lado recusa seria separar a
 * causa do efeito.
 *
 * TRES ESTADOS, NAO DOIS. `ilegivel` NAO e "desarmada": e "nao consegui ler o que voce declarou". O
 * servidor trata essa leitura como postura ATIVA (fail-closed) e o painel diz isso com todas as letras
 * — pintar de "sem postura" seria conceder por ignorancia exatamente onde o dono pode ter armado algo.
 *
 * A ASSIMETRIA NAO E ESCONDIDA. Armar E desarmar sao `human_only`. Esta interface descobre ANTES de
 * agir se ela tem sessao humana com prova de escrita (`/api/auth-info` -> `authenticated`, o mesmo
 * cookie `td_ui` que o `requireScope` exige) e, quando nao tem, desabilita os controles dizendo o
 * motivo, em vez de deixar o clique falhar com um 403 cru.
 */

/** Poll longo de proposito: a pausa global muda por decisao humana rara, nao por tick de motor. */
const POLL_MS = 60_000;

const EFEITO_LABEL: Record<EnginePostureView['efeitosBloqueados'][number], string> = {
  master_presence: 'MASTER fechado e sem auto-reabertura',
  master_cadence: 'cadencia e recuperacao do MASTER inertes',
  autonomous_session_open: 'nenhuma sessao aberta por agente ou motor',
  autonomous_session_input: 'nenhum input autonomo em terminal',
  communication_dispatch: 'dispatcher de comunicacao inerte',
  daily_briefing: 'briefing automático inerte',
  session_restore: 'restore automático suprimido no boot',
  project_operations: 'operacoes por projeto inertes (nenhuma regra dispara)',
  idle_continuation: 'rotinas de ociosidade inertes (nenhum "continue" automático)',
};

function formataDesde(desdeIso: string | null): string {
  if (desdeIso === null) return 'data desconhecida';
  const ms = Date.parse(desdeIso);
  if (!Number.isFinite(ms)) return desdeIso;
  const data = new Date(ms);
  const absoluto = data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  const minutos = Math.max(0, Math.round((Date.now() - ms) / 60_000));
  if (minutos < 1) return `${absoluto} (agora)`;
  if (minutos < 60) return `${absoluto} (há ${minutos} min)`;
  const horas = Math.round(minutos / 60);
  if (horas < 48) return `${absoluto} (há ${horas} h)`;
  return `${absoluto} (há ${Math.round(horas / 24)} d)`;
}

/**
 * Traduz a recusa para a linguagem do operador. Os tres codigos que importam aqui sao os tres jeitos
 * distintos de esta acao ser negada — e cada um pede uma acao diferente de quem esta lendo.
 */
function erroLegivel(causa: unknown): string {
  if (causa instanceof ApiError) {
    if (causa.code === 'human_only') {
      return 'recusado: pausar e retomar a autonomia é human-only. Um agente com token nunca faz isso — se ele pudesse, se liberaria no mesmo turno.';
    }
    if (causa.code === 'forbidden_csrf' || causa.status === 401) {
      return 'esta interface não tem sessão humana válida (cookie td_ui). Recarregue a página para reobter a sessão.';
    }
    if (causa.code === 'engine_posture_unavailable') {
      return 'o servidor não tem o armazenamento da pausa global disponível — nada foi alterado.';
    }
  }
  return causa instanceof Error ? causa.message : String(causa);
}

export function EnginePosturePanel({
  onStateChange,
}: {
  onStateChange?: (view: EnginePostureView) => void;
} = {}): JSX.Element | null {
  const [postura, setPostura] = useState<EnginePostureView | null>(null);
  const [podeAgir, setPodeAgir] = useState<boolean | null>(null);
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [armando, setArmando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const recarregar = useCallback(async (): Promise<void> => {
    try {
      const view = await api.getEnginePosture();
      setPostura(view);
      setErroLeitura(null);
    } catch (causa) {
      setErroLeitura(erroLegivel(causa));
    }
  }, []);

  useEffect(() => {
    void recarregar();
    const timer = setInterval(() => void recarregar(), POLL_MS);
    return () => clearInterval(timer);
  }, [recarregar]);

  useEffect(() => {
    if (postura) onStateChange?.(postura);
  }, [onStateChange, postura]);

  useEffect(() => {
    // `authenticated` = cookie td_ui valido = ator `human` COM writeProof. E a mesma prova que o
    // servidor exige; por isso da para antecipar a recusa sem inventar um endpoint novo.
    api
      .authInfo()
      .then((info) => setPodeAgir(info.authenticated))
      .catch(() => setPodeAgir(null));
  }, []);

  const armar = async (): Promise<void> => {
    const texto = motivo.trim();
    if (texto.length === 0) return;
    setOcupado(true);
    setErroAcao(null);
    try {
      setPostura(await api.setEnginePosture(texto));
      setMotivo('');
      setArmando(false);
    } catch (causa) {
      setErroAcao(erroLegivel(causa));
    } finally {
      setOcupado(false);
    }
  };

  const desarmar = async (): Promise<void> => {
    setOcupado(true);
    setErroAcao(null);
    try {
      const r = await api.clearEnginePosture();
      setPostura(r.enginePosture);
    } catch (causa) {
      setErroAcao(erroLegivel(causa));
    } finally {
      setOcupado(false);
    }
  };

  // Falha de leitura ANTES de qualquer estado conhecido: dizer o que houve, nunca desenhar "desarmada"
  // (o painel inteiro existe para nao afirmar ausencia de postura por ausencia de informacao).
  if (postura === null) {
    return erroLeitura === null ? null : (
      <section
        className="ep-panel ep-erro"
        aria-label="autonomia global"
        data-testid="engine-posture-panel"
      >
        <span className="ep-estado" data-testid="engine-posture-estado">
          AUTONOMIA GLOBAL: não foi possível ler
        </span>
        <p className="ep-motivo">{erroLeitura}</p>
      </section>
    );
  }

  const bloqueada = podeAgir === false;
  const classe = postura.ilegivel ? 'ep-ilegivel' : postura.ativa ? 'ep-armada' : 'ep-livre';

  return (
    <section
      className={`ep-panel ${classe}`}
      aria-label="autonomia global"
      data-testid="engine-posture-panel"
    >
      <div className="ep-head">
        <span className="ep-estado" data-testid="engine-posture-estado">
          {postura.ilegivel
            ? 'AUTONOMIA GLOBAL: estado ILEGÍVEL — EM REPOUSO por segurança'
            : postura.ativa
              ? 'AUTONOMIA GLOBAL: EM REPOUSO'
              : 'AUTONOMIA GLOBAL: ATIVA'}
        </span>
        {!postura.ativa && !armando && (
          <button
            type="button"
            className="hdr-btn ep-btn"
            data-testid="engine-posture-arm"
            onClick={() => {
              setErroAcao(null);
              setArmando(true);
            }}
            disabled={bloqueada}
            title={
              bloqueada
                ? 'sem sessão humana nesta interface: o servidor recusaria (human-only)'
                : 'fechar o MASTER e impedir toda interação autônoma até você retomar'
            }
          >
            pausar autonomia
          </button>
        )}
        {postura.ativa && (
          <button
            type="button"
            className="hdr-btn ep-btn"
            data-testid="engine-posture-disarm"
            onClick={() => void desarmar()}
            disabled={bloqueada || ocupado}
            title={
              bloqueada
                ? 'sem sessão humana nesta interface: o servidor recusaria (human-only)'
                : 'liberar novamente os agentes e o MASTER, preservando as configurações atuais'
            }
          >
            {ocupado ? 'retomando…' : 'retomar autonomia'}
          </button>
        )}
      </div>

      {postura.ativa && !postura.ilegivel && (
        <>
          <p className="ep-motivo" data-testid="engine-posture-motivo-declarado">
            <span className="ep-rotulo">motivo:</span> {postura.motivo ?? '(sem motivo registrado)'}
          </p>
          <p className="ep-meta">
            pausada por <strong>{postura.ator ?? 'desconhecido'}</strong> · desde{' '}
            <span title={postura.desdeIso ?? ''} data-testid="engine-posture-desde">
              {formataDesde(postura.desdeIso)}
            </span>
          </p>
        </>
      )}

      {postura.ilegivel && (
        <p className="ep-motivo">
          há estado em disco que o servidor não conseguiu ler. Enquanto for assim, abertura, input,
          restore e MASTER ficam bloqueados por segurança (fail-closed). Retome pela chave para recriar
          um estado legível.
        </p>
      )}

      {postura.ativa && (
        <details className="ep-acoes" open>
          <summary>
            {postura.efeitosBloqueados.length} efeitos bloqueados pelo repouso global
          </summary>
          <ul className="ep-acoes-lista">
            {postura.efeitosBloqueados.map((efeito) => (
              <li key={efeito}>
                <span>{EFEITO_LABEL[efeito]}</span>
              </li>
            ))}
          </ul>
          <p className="ep-nota">
            Seus terminais manuais continuam utilizáveis. As preferências dos motores não são apagadas:
            ao retomar, cada uma volta a valer exatamente como estava.
          </p>
        </details>
      )}

      {armando && (
        <div className="ep-form">
          <label className="ep-label" htmlFor="ep-motivo-input">
            por que toda a autonomia deve ficar em repouso (aparece na recusa e no audit)
          </label>
          <textarea
            id="ep-motivo-input"
            className="ep-input"
            data-testid="engine-posture-motivo"
            value={motivo}
            maxLength={500}
            rows={2}
            placeholder="ex.: não estou usando o software até concluir as próximas validações"
            onChange={(e) => setMotivo(e.target.value)}
          />
          <div className="ep-form-acoes">
            <button
              type="button"
              className="hdr-btn ep-btn"
              data-testid="engine-posture-confirm"
              onClick={() => void armar()}
              disabled={motivo.trim().length === 0 || ocupado}
              title={
                motivo.trim().length === 0
                  ? 'o motivo é obrigatório: pausa sem motivo vira "está desligado e ninguém sabe por quê"'
                  : 'pausar toda a autonomia'
              }
            >
              {ocupado ? 'pausando…' : 'confirmar pausa'}
            </button>
            <button
              type="button"
              className="hdr-btn ep-btn"
              onClick={() => {
                setArmando(false);
                setMotivo('');
                setErroAcao(null);
              }}
              disabled={ocupado}
            >
              cancelar
            </button>
          </div>
        </div>
      )}

      {bloqueada && (
        <p className="ep-nota ep-nota-alerta" data-testid="engine-posture-sem-humano">
          pausar e retomar são human-only e esta interface não tem sessão humana válida — o servidor
          recusaria. Recarregue a página para reobter a sessão.
        </p>
      )}
      {erroAcao !== null && (
        <p className="ep-erro-acao" role="alert" data-testid="engine-posture-erro">
          {erroAcao}
        </p>
      )}
      {erroLeitura !== null && <p className="ep-nota">última leitura falhou: {erroLeitura}</p>}
    </section>
  );
}

export default EnginePosturePanel;
